'use client';

import { useEffect, useRef } from 'react';

import type { ChangeKind } from './grading';
import type { ChangeLevel, MilestoneTier } from './tokens';
import { changeLevel, milestoneCrossing } from './grading';
import { useMotionReduced } from './use-motion-reduced';

const LAST_SEEN_STORAGE_KEY = 'savvy-kit.motion.last-seen';
const CELEBRATED_STORAGE_KEY = 'savvy-kit.motion.celebrated';

type Celebration =
  | { readonly kind: 'none' }
  | {
      readonly kind: 'celebrate';
      readonly from: number;
      readonly level: ChangeLevel;
      readonly tier: MilestoneTier | null;
    };

const NO_CELEBRATION: Celebration = { kind: 'none' };

type Ledger = Readonly<Record<string, number>>;

function localStore(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

function sessionStore(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

function readLedger(storage: Storage | null, storageKey: string): Ledger {
  if (storage === null) return {};
  try {
    const raw = storage.getItem(storageKey);
    if (raw === null) return {};
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return {};
    return Object.fromEntries(
      Object.entries(parsed).filter((entry): entry is [string, number] => Number.isFinite(entry[1])),
    );
  } catch {
    return {};
  }
}

function writeLedger(storage: Storage | null, storageKey: string, ledger: Ledger): void {
  if (storage === null) return;
  try {
    storage.setItem(storageKey, JSON.stringify(ledger));
  } catch {
    // Storage blocked or full: the worst case is the same celebration playing again next visit.
  }
}

type DecideCelebrationArgs = {
  readonly key: string;
  readonly value: number;
  readonly kind?: ChangeKind;
};

let celebrationEnabled = true;

function setCelebrationEnabled(enabled: boolean): void {
  celebrationEnabled = enabled;
}

// Demo mode: every load celebrates, whatever the session ledger says, so the coins are on screen for a
// story or a walkthrough. Off for real clients, who celebrate only a rise the ledger has not yet paid out.
let demoAlwaysCelebrate = false;

function setDemoAlwaysCelebrate(enabled: boolean): void {
  demoAlwaysCelebrate = enabled;
}

function decideCelebration({ key, value, kind = 'currency' }: DecideCelebrationArgs): Celebration {
  if (!celebrationEnabled) return NO_CELEBRATION;
  if (!Number.isFinite(value)) return NO_CELEBRATION;

  if (demoAlwaysCelebrate) {
    // Subtract a magnitude, not a fraction: `value * 0.95` sits above a negative balance, and then this
    // disagrees with `GainNumber` about which way the figure is travelling.
    const from = value - Math.abs(value) * 0.05;
    const level = changeLevel(from, value, kind);
    const tier = milestoneCrossing(from, value);
    return { kind: 'celebrate', from, level, tier };
  }

  const lastSeen = readLedger(localStore(), LAST_SEEN_STORAGE_KEY)[key];
  const celebratedAt = readLedger(sessionStore(), CELEBRATED_STORAGE_KEY)[key];
  if (lastSeen === undefined || celebratedAt !== undefined || value <= lastSeen) return NO_CELEBRATION;
  const level = changeLevel(lastSeen, value, kind);
  const tier = milestoneCrossing(lastSeen, value);
  if (level.coins === 0 && tier === null) return NO_CELEBRATION;
  return { kind: 'celebrate', from: lastSeen, level, tier };
}

type RecordSeenArgs = {
  readonly key: string;
  readonly value: number;
  readonly celebrated: boolean;
};

/** Remembers `value` as the figure the client has now seen; a celebration also closes this session for `key`. */
function recordSeen({ key, value, celebrated }: RecordSeenArgs): void {
  if (!Number.isFinite(value)) return;
  const local = localStore();
  writeLedger(local, LAST_SEEN_STORAGE_KEY, { ...readLedger(local, LAST_SEEN_STORAGE_KEY), [key]: value });
  if (celebrated) {
    const session = sessionStore();
    writeLedger(session, CELEBRATED_STORAGE_KEY, {
      ...readLedger(session, CELEBRATED_STORAGE_KEY),
      [key]: Date.now(),
    });
  }
}

function removeLedger(storage: Storage | null, storageKey: string): void {
  if (storage === null) return;
  try {
    storage.removeItem(storageKey);
  } catch {
    return;
  }
}

function clearCelebrationLedgers(): void {
  removeLedger(localStore(), LAST_SEEN_STORAGE_KEY);
  removeLedger(sessionStore(), CELEBRATED_STORAGE_KEY);
}

type UseSessionCelebrationArgs = {
  /** Names the figure across visits, e.g. `home-investments:<householdId>`. `null` while unknown. */
  readonly key: string | null;
  readonly value: number | null | undefined;
  readonly kind?: ChangeKind;
};

/**
 * Decides once, when the figure first arrives, whether this visit celebrates, and records the figure as seen so
 * the next visit grades against it. The decision is held for the life of the mount so a refetch cannot replay
 * it. Reduced motion never celebrates.
 */
function useSessionCelebration({ key, value, kind = 'currency' }: UseSessionCelebrationArgs): Celebration {
  const reduced = useMotionReduced();
  const decidedRef = useRef<{ readonly key: string; readonly decision: Celebration } | null>(null);
  const settled =
    key !== null && value !== null && value !== undefined && Number.isFinite(value) && typeof window !== 'undefined';

  if (settled && decidedRef.current?.key !== key) {
    decidedRef.current = { key, decision: reduced ? NO_CELEBRATION : decideCelebration({ key, value, kind }) };
  }
  const decision = decidedRef.current?.key === key ? decidedRef.current.decision : NO_CELEBRATION;
  const celebrated = decision.kind === 'celebrate';

  useEffect(() => {
    if (key === null || value === null || value === undefined || !Number.isFinite(value)) return;
    recordSeen({ key, value, celebrated });
  }, [key, value, celebrated]);

  return decision;
}

type CelebrationFigureProps = {
  readonly from: number | undefined;
  readonly capTravel: boolean;
  readonly coins: boolean;
};

/**
 * The `GainNumber` props a celebration decides: where the count starts, whether its travel is capped, whether
 * coins pay.
 *
 * `active` is false once the figure the celebration was decided on is no longer the one on screen — a range
 * switch, say. Without it the count keeps opening at the stale last-seen balance instead of the new window's
 * own opening value, for as long as the card stays mounted.
 */
function celebrationFigureProps(
  celebration: Celebration,
  from: number | undefined,
  active = true,
): CelebrationFigureProps {
  if (active && celebration.kind === 'celebrate') return { from: celebration.from, capTravel: false, coins: true };
  return { from, capTravel: true, coins: false };
}

export {
  CELEBRATED_STORAGE_KEY,
  celebrationFigureProps,
  clearCelebrationLedgers,
  decideCelebration,
  LAST_SEEN_STORAGE_KEY,
  recordSeen,
  setCelebrationEnabled,
  setDemoAlwaysCelebrate,
  useSessionCelebration,
};
export type { Celebration, CelebrationFigureProps };
